import { useTranslation } from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { Icon, IconSize, IconVariants } from "@next-platform/client-common-spicy-ui";
import { Textarea } from "@nextui-org/react";
import { useTheme as useNextTheme } from "next-themes";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

import { Col, Row, SpicyButton, Text } from "@/components/customComponents";

import Trans from "../Trans/Trans";
import MemoryEditorDropdown from "./MemoryEditorDrowndown";

const MAX_MEMORY_SIZE = 250;
const logger = appLogger.withTag("MemoryEditor");

const MemoryEditor = ({
  memoryId,
  memoryText = "",
  isPinned,
  memoryType,
  onSave,
  onDelete,
  onCancel,
  onPinnedToggle,
  currentlyEditing,
  setCurrentlyEditing,
}) => {
  const { t } = useTranslation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [memoryEditText, setMemoryEditText] = useState(memoryText);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { theme } = useNextTheme();

  useEffect(() => {
    setMemoryEditText(memoryText);
  }, [memoryText]);

  const saveMemory = async () => {
    setError(null);
    setIsLoading(true);
    await onSave(memoryId, memoryEditText)
      .then(() => {
        setIsLoading(false);
        setIsEditMode(false);
      })
      .catch((error) => {
        logger.error("Failed to save memory", error);
        setError(error?.userMessage || error?.message);
        setIsLoading(false);
      });
  };

  const handleCancel = () => {
    setMemoryEditText(null);
    setCurrentlyEditing(null);
    setIsEditMode(false);
    onCancel();
  };

  const handleDelete = () => {
    onDelete(memoryId);
  };

  const handleEdit = () => {
    setCurrentlyEditing(memoryId);
    setIsEditMode(true);
  };

  const handlePinnedToggle = () => {
    onPinnedToggle(memoryId, !isPinned);
  };

  useEffect(() => {
    if (memoryId === "new")
      //To add new memory
      setIsEditMode(true);
  }, [memoryId]);

  useEffect(() => {
    if (currentlyEditing !== memoryId) {
      setIsEditMode(false);
    }
  }, [currentlyEditing, memoryId]);

  const isMemoryTooLong = memoryEditText?.length > MAX_MEMORY_SIZE;
  const userMemoryCardColor = theme == "dark" ? "bg-slate-700" : "bg-sky-200";
  return (
    <Col className={`w-full rounded-2xl p-2 ${memoryType === "user" ? userMemoryCardColor : ""}`}>
      {!isEditMode && (
        <Row>
          {isPinned ? <Icon icon={IconVariants.PIN} size={IconSize.XSMALL} /> : null}
          <Col className="w-full [overflow-wrap:anywhere]">
            <Text variant="sm">{memoryText}</Text>
          </Col>
          <Col align="end" className="w-4">
            {!currentlyEditing && (
              <MemoryEditorDropdown onEdit={handleEdit} onDelete={handleDelete} onPinnedToggle={handlePinnedToggle} isPinned={isPinned} />
            )}
          </Col>
        </Row>
      )}
      {isEditMode ? (
        <>
          <Row>
            <Textarea
              classNames={{ input: "text-[16px] scale-[87.5%] origin-top-left min-w-[114%] min-h-[110%]" }}
              value={memoryEditText}
              onValueChange={setMemoryEditText}
              placeholder={t("chat:modal.memories.addMemoryInput.placeholder")}
              maxLength={MAX_MEMORY_SIZE}
            />
          </Row>
          {!!(error || isMemoryTooLong) && (
            <Row justify="end" className="pt-2">
              <Text variant="sm" type="error">
                {error || (isMemoryTooLong ? <Trans i18nKey="chat:modal.memories.validation.tooLong" /> : null)}
              </Text>
            </Row>
          )}
          <Row className="pt-2">
            <Col>
              <Row>
                <Text variant="xs">
                  {memoryEditText?.length} / {MAX_MEMORY_SIZE}
                </Text>
              </Row>
            </Col>
            <Col align="end">
              <Row>
                <SpicyButton
                  isDisabled={memoryEditText === "" || isLoading || isMemoryTooLong || memoryText === memoryEditText}
                  variant="secondary_short"
                  color="primary_medium"
                  onPress={saveMemory}
                >
                  {isLoading ? (
                    <Trans i18nKey="common:saving" />
                  ) : memoryId !== "new" ? (
                    <Trans i18nKey="common:save" />
                  ) : (
                    <Trans i18nKey="chat:modal.memories.cta.add" />
                  )}
                </SpicyButton>
                <SpicyButton
                  isIconOnly
                  variant="tertiary_small_short"
                  color="primary"
                  startContent={<Icon icon={IconVariants.CLOSE_CIRCLE} size={IconSize.SMALL} />}
                  onPress={handleCancel}
                />
              </Row>
            </Col>
          </Row>

          <Row justify="end" className="pt-2"></Row>
        </>
      ) : null}
    </Col>
  );
};

MemoryEditor.propTypes = {
  memoryId: PropTypes.string,
  memoryText: PropTypes.string,
  isPinned: PropTypes.bool,
  memoryType: PropTypes.string,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  onDelete: PropTypes.func,
  onPinnedToggle: PropTypes.func,
  currentlyEditing: PropTypes.string,
  setCurrentlyEditing: PropTypes.func,
};
export default MemoryEditor;
